import React, { useState, useEffect, useRef } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { connect } from "react-redux";
import {
  payBillUrl,
  getViewUserInfoUrl,
  getDirectPayWalletUrl,
  getDirectPayCardListRequestUrl,
  getDirectPayCardDeleteUrl,
  getDirectPayCardPayUrl,
  getDirectPayCardAddUrl,
} from "../../redux/URL";
import axiosInstance from "../../redux/axiosInstance";
import { getPaymentDetails, getPaymentDetailsPrepaid, isPrepaid } from "../../redux/action/accountDetails.action";
import successLog from "../../assests/images/success.png";
import errorLog from "../../assests/images/error.png";
import visaIcon from "../../assests/images/visa-icon.png";
import masterIcon from "../../assests/images/master-icon.png";

const formatDisplayAmountLKR = (value) =>
  Number(value ?? 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const MINIMUM_PAYMENT_AMOUNT = 100;
const PAYMENT_AMOUNT_ERROR_MSG =
  "Payment amount must be greater than or equal to Rs. 100.";

const normalizeAmountForInput = (value) => {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue.toFixed(2) : "";
};

const getPaymentAmountStorageKey = (telephone) =>
  `payment_method_amount_${telephone || "default"}`;

const PaymentMethod = ({
  payments,
  account,
  loadingPayments,
  service_list,
  getPaymentDetails,
  getPaymentDetailsPrepaid,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { amount, mobile: initialMobile } = location.state || {};
  
  // Get current telephone number from localStorage (updated when user changes in top dropdown)
  const getCurrentTelephone = () => {
    return localStorage.getItem("selectedAcc") || initialMobile || "";
  };
  
  // Store initial telephone number from location.state (when user first arrives)
  const initialTelephone = initialMobile || getCurrentTelephone();
  
  const [selected, setSelected] = useState("directPay");
  const [cards, setCards] = useState([]);
  const [loadingCards, setLoadingCards] = useState(true);
  const [error, setError] = useState(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [showPaymentAlert, setShowPaymentAlert] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentMessage, setPaymentMessage] = useState("");
  const [paymentOrderId, setPaymentOrderId] = useState("");
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [deleteStatus, setDeleteStatus] = useState(null);
  const [deleteMessage, setDeleteMessage] = useState("");
  const [deletingCard, setDeletingCard] = useState(null);
  const [showConfirmAlert, setShowConfirmAlert] = useState(false);
  const [cardToDelete, setCardToDelete] = useState(null);
  const [confirmMessage, setConfirmMessage] = useState("");
  const [showPaymentConfirmAlert, setShowPaymentConfirmAlert] = useState(false);
  const [paymentConfirmMessage, setPaymentConfirmMessage] = useState("");
  const [walletId, setWalletId] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentAmountTouched, setPaymentAmountTouched] = useState(false);
  const initializedAmountTelephoneRef = useRef("");
  const skipDueAmountInitializationRef = useRef(true);
  
  // State to track current telephone number (updates when user changes in top dropdown)
  const [currentMobile, setCurrentMobile] = useState(getCurrentTelephone());

  useEffect(() => {
    fetchWalletId();
    fetchUserDetails();
  }, []);

  // Watch for changes in selected account (when user changes telephone in top dropdown)
  useEffect(() => {
    const checkTelephoneChange = () => {
      const newTelephone = getCurrentTelephone();
      if (newTelephone !== currentMobile) {
        setCurrentMobile(newTelephone);
      }
    };

    // Check immediately
    checkTelephoneChange();

    // Listen for storage changes (when user changes telephone in top dropdown)
    const handleStorageChange = (e) => {
      if (e.key === 'selectedAcc') {
        checkTelephoneChange();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Also check periodically in case storage event doesn't fire (same-origin updates)
    const interval = setInterval(checkTelephoneChange, 500);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      clearInterval(interval);
    };
  }, [currentMobile]);

  // Function to get account number from account list
  const getAccountNumber = () => {
    if (!account || account.length === 0) return null;
    const selectedTel = getCurrentTelephone();
    const foundAccount = account.find(acc => acc.telephoneno === selectedTel);
    return foundAccount ? foundAccount.accountno : null;
  };

  // Fetch payment details when telephone number changes
  useEffect(() => {
    const telephone = getCurrentTelephone();
    const accountNo = getAccountNumber();
    
    if (telephone && accountNo) {
      // Check if it's prepaid or postpaid
      if (service_list && isPrepaid(service_list)) {
        getPaymentDetailsPrepaid(telephone);
      } else {
        getPaymentDetails(telephone, accountNo);
      }
    }
  }, [
    currentMobile,
    account,
    service_list,
    getPaymentDetails,
    getPaymentDetailsPrepaid,
  ]); // Fetch when telephone or account changes

  const dueAmount =
    payments &&
    payments.listofbillingInquiryType &&
    payments.listofbillingInquiryType.length > 0
      ? payments.listofbillingInquiryType[0].outstandingBalance
      : "";

  useEffect(() => {
    initializedAmountTelephoneRef.current = "";
    skipDueAmountInitializationRef.current = true;
  }, [currentMobile]);

  useEffect(() => {
    if (!currentMobile) {
      return;
    }

    if (initializedAmountTelephoneRef.current === currentMobile) {
      return;
    }

    const currentTel = getCurrentTelephone();
    const telephoneChanged = currentTel !== initialTelephone;
    const routeAmount =
      !telephoneChanged && amount ? normalizeAmountForInput(amount) : "";

    if (routeAmount) {
      const storageKey = getPaymentAmountStorageKey(currentMobile);
      setPaymentAmount(routeAmount);
      sessionStorage.setItem(storageKey, routeAmount);
      initializedAmountTelephoneRef.current = currentMobile;
      return;
    }

    const storageKey = getPaymentAmountStorageKey(currentMobile);
    const storedAmount = sessionStorage.getItem(storageKey);

    if (storedAmount !== null && storedAmount !== "") {
      setPaymentAmount(storedAmount);
      initializedAmountTelephoneRef.current = currentMobile;
      return;
    }

    if (skipDueAmountInitializationRef.current) {
      skipDueAmountInitializationRef.current = false;
      return;
    }

    if (!loadingPayments && dueAmount !== "") {
      const nextAmount = normalizeAmountForInput(dueAmount);
      setPaymentAmount(nextAmount);
      sessionStorage.setItem(storageKey, nextAmount);
      initializedAmountTelephoneRef.current = currentMobile;
    }
  }, [amount, currentMobile, dueAmount, initialTelephone, loadingPayments]);

  useEffect(() => {
    if (!currentMobile) {
      return;
    }

    const storageKey = getPaymentAmountStorageKey(currentMobile);
    if (paymentAmount === "") {
      sessionStorage.removeItem(storageKey);
      return;
    }

    sessionStorage.setItem(storageKey, paymentAmount);
  }, [currentMobile, paymentAmount]);

  const paymentAmountValue = Number(paymentAmount);
  const hasValidPaymentAmount =
    paymentAmount !== "" && Number.isFinite(paymentAmountValue);
  const payableAmount = hasValidPaymentAmount ? paymentAmountValue : 0;
  const paymentAmountError =
    paymentAmount !== "" && payableAmount < MINIMUM_PAYMENT_AMOUNT
      ? PAYMENT_AMOUNT_ERROR_MSG
      : "";

  const handlePaymentAmountChange = (event) => {
    setPaymentAmountTouched(true);
    setPaymentAmount(event.target.value);
  };

  const handlePaymentAmountBlur = () => {
    setPaymentAmountTouched(true);
    if (!hasValidPaymentAmount) {
      return;
    }

    setPaymentAmount(normalizeAmountForInput(paymentAmountValue));
  };

  // Handle payment redirect from return2.php
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const status = urlParams.get('status');
    const orderId = urlParams.get('orderId');
    const desc = urlParams.get('desc') || urlParams.get('description');
    const trnId = urlParams.get('trnId');

    // Check if we're returning from payment
    if (status) {
      // Clear URL parameters to prevent re-processing on refresh
      window.history.replaceState({}, document.title, window.location.pathname);

      if (status === 'SUCCESS') {
        showCustomAlert('success', desc || 'Your payment has been processed successfully!');
        // Refresh card list
        fetchCardList();
      } else if (status === 'FAILED') {
        showCustomAlert('error', `Payment Failed: ${desc || 'Transaction failed'}`);
      }
    }
  }, []); // Run once on component mount

  const fetchWalletId = async () => {
    try {
      const walletBody = new URLSearchParams();
      walletBody.append("eventsource", getCurrentTelephone() || "");
      const response = await axiosInstance.post(
        getDirectPayWalletUrl,
        walletBody.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      // Check if response is successful (errorMessege is null) and has walletid
      if (
        response.data &&
        response.data.errorMessege === null &&
        response.data.dataBundle &&
        response.data.dataBundle.length > 0 &&
        response.data.dataBundle[0].walletid
      ) {
        const walletIdValue = response.data.dataBundle[0].walletid;
        setWalletId(walletIdValue);
        // Call cardlist API only if walletId is not empty
        fetchCardList(walletIdValue);
      } else {
        // Wallet ID is empty or not available
        setLoadingCards(false);
        setCards([]);
        setError(null); // Clear any previous errors
      }
    } catch (err) {
      console.error("Error fetching wallet ID:", err);
      // Set loading to false on error
      setLoadingCards(false);
      setCards([]);
      setError("Unable to retrieve wallet information. Please try again.");
    }
  };

  const fetchUserDetails = async () => {
    try {
      const response = await axiosInstance.get(getViewUserInfoUrl);
      if (response.data.isSuccess && response.data.dataBundle) {
        const userType = localStorage.getItem("userType");
        const userName = localStorage.getItem("userName");
        const altContact = response.data.dataBundle.altrContact;

        if (userType === "MOBILE") {
          // If logged in with mobile, userName is mobile, altContact is email
          setUserPhone(userName || "");
          setUserEmail(altContact || "");
        } else {
          // If logged in with email, userName is email, altContact is mobile
          setUserEmail(userName || "");
          setUserPhone(altContact || "");
        }
      }
    } catch (err) {
      console.error("Error fetching user details:", err);
      // Fallback to localStorage values if API fails
      const userType = localStorage.getItem("userType");
      const userName = localStorage.getItem("userName");
      if (userType === "MOBILE") {
        setUserPhone(userName || "");
      } else {
        setUserEmail(userName || "");
      }
    }
  };

  const fetchCardList = async (walletIdParam = null) => {
    try {
      setLoadingCards(true);
      setError(null);

      const user_name = localStorage.getItem("userName");

      if (!user_name) {
        setError("User not logged in");
        setLoadingCards(false);
        return;
      }

      // Use parameter if provided, otherwise use state (for manual calls)
      const walletIdToUse = walletIdParam || walletId;

      if (!walletIdToUse) {
        // Wallet ID not available - show empty state
        setLoadingCards(false);
        setCards([]);
        setError(null);
        // Set to Direct Pay when wallet ID not available
        setSelected("directPay");
        return;
      }

      const formData = new URLSearchParams();
      formData.append("user_name", user_name);
      formData.append("wallet_id", walletIdToUse);
      formData.append("eventsource", getCurrentTelephone() || "");
      const response = await axiosInstance.post(getDirectPayCardListRequestUrl, formData.toString(), {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
      });

      const data = response.data;

      // Updated to use dataBundle instead of data (API response structure changed)
      if (data.isSuccess && data.dataBundle && data.dataBundle.card_list) {
        const cardList = data.dataBundle.card_list || [];
        
        if (cardList.length > 0) {
          const apiCards = cardList.map((card) => {
            const lastFour = card.mask ? card.mask.slice(-4) : "0000";

            const cardId = card.card_id ? card.card_id.toString() : "0";

            let formattedExp = card.expiry || "01/2025";
            if (card.expiry && card.expiry.includes("-")) {
              const [month, year] = card.expiry.split("-");
              formattedExp = `${month}/20${year}`;
            }

            // Normalize card brand name
            let cardBrand = (card.brand || "VISA").toUpperCase();
            if (cardBrand === "MASTER" || cardBrand === "MASTERCARD") {
              cardBrand = "MASTERCARD";
            } else if (cardBrand === "AMEX" || cardBrand === "AMERICAN EXPRESS" || cardBrand === "AMERICANEXPRESS") {
              cardBrand = "AMEX";
            } else if (cardBrand === "VISA") {
              cardBrand = "VISA";
            }

            return {
              id: cardId,
              type: cardBrand,
              masked: `**** **** ${lastFour}`,
              exp: formattedExp,
              original: card,
            };
          });

          setCards(apiCards);

          if (apiCards.length > 0) {
            setSelected(apiCards[0].id);
          }
        } else {
          // Empty card list - show empty state
          setCards([]);
          setError(null);
          // Set to Direct Pay when no cards available
          setSelected("directPay");
        }
      } else {
        // API returned success but no card_list - treat as empty
        setCards([]);
        setError(null);
        // Set to Direct Pay when no cards available
        setSelected("directPay");
      }
    } catch (err) {
      console.error("Error fetching cards:", err);
      setError("Network error. Please try again.");
      setCards([]);
      // Set to Direct Pay when error occurs and no cards available
      setSelected("directPay");
    } finally {
      setLoadingCards(false);
    }
  };

  const handleDeleteCard = (cardId) => {
    const cardToDelete = cards.find((card) => card.id === cardId);
    if (!cardToDelete) return;

    // Show custom confirmation alert
    setCardToDelete(cardToDelete);
    setConfirmMessage(
      `Are you sure you want to delete card ending with ${cardToDelete.masked.slice(
        -4
      )}?`
    );
    setShowConfirmAlert(true);
  };

  const confirmDelete = async () => {
    if (!cardToDelete) return;

    setShowConfirmAlert(false);
    setDeletingCard(cardToDelete.id);
    await deleteCardApi(cardToDelete.id);
  };

  const cancelDelete = () => {
    setShowConfirmAlert(false);
    setCardToDelete(null);
    setConfirmMessage("");
  };

  const deleteCardApi = async (cardId) => {
    try {
      const formData = new URLSearchParams();
      formData.append("card_id", cardId);
      formData.append("eventsource", getCurrentTelephone() || "");

      const response = await axiosInstance.post(
        getDirectPayCardDeleteUrl,
        formData.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const data = response.data;
      console.log("Delete card response:", data);

      // Updated to match new API response structure
      if (data.isSuccess && data.dataBundle && data.dataBundle.status === "success") {
        // Show success message
        showDeleteAlertMessage("success", "Card deleted successfully!");
        
        // Refresh the page after a short delay to show the success message
        setTimeout(() => {
          window.location.reload();
        }, 1500);
      } else {
        const errorMessage = data.errorShow || "Failed to delete card. Please try again.";
        showDeleteAlertMessage("error", errorMessage);
      }
    } catch (err) {
      console.error("Error deleting card:", err);
      showDeleteAlertMessage("error", "Network error. Please try again.");
    } finally {
      setDeletingCard(null);
      setCardToDelete(null);
    }
  };

  const showDeleteAlertMessage = (type, message) => {
    setDeleteStatus(type);
    setDeleteMessage(message);
    setShowDeleteAlert(true);
  };

  const closeDeleteAlert = () => {
    setShowDeleteAlert(false);
    setDeleteStatus(null);
    setDeleteMessage("");
  };

  const handlePayNow = async (e) => {
    e.preventDefault();
    
    // Show confirmation popup only for saved card payments
    if (selected !== "directPay" && selected !== "boc" && selectedCard) {
      const paymentMethodName = `${selectedCard.type} **** ${selectedCard.masked.slice(-4)}`;
      
      setPaymentConfirmMessage(
        `Are you sure you want to proceed with payment?\n\n` +
        `Payment Method: ${paymentMethodName}\n` +
        `Amount: Rs. ${formatDisplayAmountLKR(payableAmount)}\n` +
        `Telephone: ${currentMobile}`
      );
      setShowPaymentConfirmAlert(true);
      return;
    }

    // For Direct Pay, process directly without confirmation
    if (selected === "directPay") {
      await processDirectPay();
      return;
    }
  };

  const confirmPayment = async () => {
    setShowPaymentConfirmAlert(false);
    
    // This will only be called for saved card payments
    if (selectedCard) {
      await processSavedCardPayment();
    }
  };

  const cancelPayment = () => {
    setShowPaymentConfirmAlert(false);
    setPaymentConfirmMessage("");
  };

  const processSavedCardPayment = async () => {
    if (
      !hasValidPaymentAmount ||
      !currentMobile ||
      payableAmount < MINIMUM_PAYMENT_AMOUNT
    ) {
      showCustomAlert(
        "error",
        "Please check amount and telephone number. Minimum amount is Rs. 100"
      );
      return;
    }

    try {
      setLoadingPayment(true);
      setError(null);

      const formData = new URLSearchParams();
      formData.append("phone", currentMobile);
      formData.append("wallet_id", walletId);
      formData.append("amount", payableAmount);
      formData.append("card_id", selectedCard.id);
      formData.append("eventsource", currentMobile);

      const response = await axiosInstance.post(
        getDirectPayCardPayUrl,
        formData.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const data = response.data;
      console.log("Card payment response:", data);

      if (data.isSuccess && data.dataBundle) {
        try {
          const paymentResult =
            typeof data.dataBundle === "string"
              ? JSON.parse(data.dataBundle)
              : data.dataBundle;

          const status = paymentResult.status || "unknown";
          const message = paymentResult.message || "Payment processed";
          const orderId = paymentResult.order_id || "";

          setPaymentOrderId(orderId);

          if (status === "success" || status === "completed") {
            showCustomAlert(
              "success",
              "Your payment has been processed successfully!"
            );
            fetchCardList();
            const telephone = getCurrentTelephone();
            const accountNo = getAccountNumber();
            if (
              service_list &&
              !isPrepaid(service_list) &&
              telephone &&
              accountNo
            ) {
              const paymentRefreshDelayMs = 2500;
              setTimeout(() => {
                getPaymentDetails(telephone, accountNo);
              }, paymentRefreshDelayMs);
            }
          } else if (status === "declined") {
            showCustomAlert("error", `Payment Declined: ${message}`);
          } else {
            showCustomAlert("error", `Payment Failed: ${message}`);
          }
        } catch (parseError) {
          console.error("Error parsing payment response:", parseError);
          showCustomAlert("error", "Error processing payment response");
        }
      } else {
        showCustomAlert("error", data.errorShow || "Payment failed");
      }
    } catch (err) {
      console.error("Error processing card payment:", err);
      showCustomAlert("error", "Network error. Please try again.");
    } finally {
      setLoadingPayment(false);
    }
  };

  const processDirectPay = async () => {
    if (
      !hasValidPaymentAmount ||
      !currentMobile ||
      payableAmount < MINIMUM_PAYMENT_AMOUNT
    ) {
      showCustomAlert(
        "error",
        "Please check amount and telephone number. Minimum amount is Rs. 100"
      );
      return;
    }

    try {
      setLoadingPayment(true);
      setError(null);

      const formData = new URLSearchParams();
      const userType = localStorage.getItem("userType");
      const emailForCardAdd =
        userType === "MOBILE"
          ? `${userPhone}@slt.com.lk`
          : userEmail;
      formData.append("amount", payableAmount);
      formData.append("email", emailForCardAdd);
      formData.append("phone", userPhone);
      formData.append("eventsource", currentMobile);

      const response = await axiosInstance.post(
        getDirectPayCardAddUrl,
        formData.toString(),
        {
          headers: {
            "Content-Type": "application/x-www-form-urlencoded",
          },
        }
      );

      const data = response.data;

      if (data.isSuccess && data.dataBundle && data.dataBundle.link) {
        // Replace current window with payment URL (like Sampath PayCorp)
        // Backend will redirect to /makePayment after payment completion
        window.location.href = data.dataBundle.link;
        // Note: Code after this won't execute because page is navigating away
      } else {
        setError(data.errorShow || "Failed to initialize Direct Pay");
        setLoadingPayment(false);
      }
    } catch (err) {
      console.error("Error initiating Direct Pay:", err);
      showCustomAlert('error', "Network error. Please try again.");
      setLoadingPayment(false);
    }
  };

  const showCustomAlert = (type, message) => {
    setPaymentStatus(type);
    setPaymentMessage(message);
    setShowPaymentAlert(true);
  };

  const closePaymentAlert = () => {
    setShowPaymentAlert(false);
    setPaymentStatus(null);
    setPaymentMessage("");
    setPaymentOrderId("");
  };

  const selectedCard = cards.find((card) => card.id === selected);

  return (
    <div className="mainBody bill common-panel">
      <div className="body-container">
        <div className="title">Payment Methods</div>
        <div className="wrapper">
          <div className="mt-3">
            {/* Payment Alert View */}
            {showPaymentAlert && (
              <div className="alertView">
                <div>
                  <div className="title text-center mb-3">
                    {paymentStatus === "success"
                      ? "Payment Completed"
                      : paymentStatus === "error"
                      ? "Payment Failed"
                      : "Payment Information"}
                  </div>
                  <div className="mainText">
                    <div className="img-container">
                      {paymentStatus === "success" ? (
                        <img src={successLog} alt="Success" />
                      ) : paymentStatus === "error" ? (
                        <img src={errorLog} alt="Error" />
                      ) : (
                        <img src={successLog} alt="Info" />
                      )}
                    </div>
                    <div className="message">
                      {paymentMessage}
                    </div>
                  </div>
                  <div className="actionButton">
                    <button
                      type="button"
                      className={`btn ${
                        paymentStatus === "success"
                          ? "btn-success"
                          : paymentStatus === "error"
                          ? "btn-danger"
                          : "btn-primary"
                      } btn-ok`}
                      onClick={closePaymentAlert}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delete Card Alert View */}
            {showDeleteAlert && (
              <div className="alertView">
                <div>
                  <div className="title text-center mb-3">
                    {deleteStatus === "success"
                      ? "Card Deleted!"
                      : "Delete Failed"}
                  </div>
                  <div className="mainText">
                    <div className="img-container">
                      {deleteStatus === "success" ? (
                        <img src={successLog} alt="Success" />
                      ) : (
                        <img src={errorLog} alt="Error" />
                      )}
                    </div>
                    <div className="message">{deleteMessage}</div>
                  </div>
                  <div className="actionButton">
                    <button
                      type="button"
                      className={`btn ${
                        deleteStatus === "success"
                          ? "btn-success"
                          : "btn-danger"
                      } btn-ok`}
                      onClick={closeDeleteAlert}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Confirm Delete Alert View */}
            {showConfirmAlert && (
              <div className="alertView">
                <div>
                  <div className="title text-center mb-3">Confirm Delete</div>
                  <div className="mainText">
                    <div className="img-container">
                      <img src={errorLog} alt="Warning" />
                    </div>
                    <div className="message">{confirmMessage}</div>
                  </div>
                  <div className="actionButton">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-cancel"
                      onClick={cancelDelete}
                      style={{ marginRight: "10px", padding: "6px 20px" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-ok"
                      onClick={confirmDelete}
                      style={{ padding: "6px 26px" }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Confirm Payment Alert View */}
            {showPaymentConfirmAlert && (
              <div className="alertView">
                <div>
                  <div className="title text-center mb-3">Confirm Payment</div>
                  <div className="mainText">
                    <div className="img-container">
                      <img src={successLog} alt="Confirm" />
                    </div>
                    <div className="message" style={{ whiteSpace: "pre-line" }}>
                      {paymentConfirmMessage}
                    </div>
                  </div>
                  <div className="actionButton">
                    <button
                      type="button"
                      className="btn btn-outline-secondary btn-cancel"
                      onClick={cancelPayment}
                      style={{ marginRight: "10px", padding: "6px 20px" }}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-ok"
                      onClick={confirmPayment}
                      style={{ padding: "6px 26px" }}
                    >
                      Confirm
                    </button>
                  </div>
                </div>
              </div>
            )}

            {error && setError(null)}

            {/* Payment Methods */}
            <>
                <div className="payment-section-title">Payment Types</div>

                {/* Direct Pay Option */}
                <div className="payment-section">
                  <div
                    className={`payment-method-card ${
                      selected === "directPay" ? "selected" : ""
                    }`}
                    onClick={() => setSelected("directPay")}
                  >
                    <div className="payment-card-left">
                      <div className="payment-card-icon directPay-icon">
                        <span>💰</span>
                      </div>
                      <div className="payment-card-details">
                        <div className="payment-card-number">Direct Pay</div>
                        <div className="payment-card-expiry">
                          Remember this card for next time?
                        </div>
                      </div>
                    </div>
                    <div className="payment-card-right">
                      <div className="payment-radio-container">
                        <input
                          type="radio"
                          name="paymentMethod"
                          checked={selected === "directPay"}
                          onChange={() => setSelected("directPay")}
                          className="payment-method-radio"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bank Payment Option */}
                <div className="payment-section">
                  <div
                    className={`payment-method-card ${
                      selected === "boc" ? "selected" : ""
                    }`}
                    onClick={() => setSelected("boc")}
                  >
                    <div className="payment-card-left">
                      <div className="payment-card-icon bank-icon">
                        <span>🏦</span>
                      </div>
                      <div className="payment-card-details">
                        <div className="payment-card-number">
                          BOC/Nations Trust Bank/WEBXPAY
                        </div>
                        <div className="payment-card-expiry">
                          Enter card details for each payment
                        </div>
                      </div>
                    </div>
                    <div className="payment-card-right">
                      <div className="payment-radio-container">
                        <input
                          type="radio"
                          name="paymentMethod"
                          checked={selected === "boc"}
                          onChange={() => setSelected("boc")}
                          className="payment-method-radio"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Divider - Only show if we have cards or are loading */}
                {(cards.length > 0 || loadingCards) && (
                  <div className="payment-divider">
                    <span className="divider-text">Or Saved Cards</span>
                    {loadingCards && (
                      <small className="ms-2 text-muted">(Loading...)</small>
                    )}
                  </div>
                )}

                {/* Saved Cards List */}
                {loadingCards ? (
                  <div className="payment-section">
                    <div className="text-center py-4">
                      <div
                        className="spinner-border text-primary"
                        role="status"
                      />
                      <p className="mt-2">Loading saved cards...</p>
                    </div>
                  </div>
                ) : cards.length > 0 ? (
                  <div className="payment-section">
                    <div className="payment-methods-list">
                      {cards.map((card) => (
                        <div
                          className={`payment-method-card ${
                            selected === card.id ? "selected" : ""
                          }`}
                          key={card.id}
                          onClick={() => setSelected(card.id)}
                        >
                          <div className="payment-card-left">
                            <div
                              className={`payment-card-icon ${
                                card.type === "MASTERCARD"
                                  ? "master-icon"
                                  : card.type === "VISA"
                                  ? "visa-icon"
                                  : card.type === "AMEX"
                                  ? "amex-icon"
                                  : ""
                              }`}
                            >
                              {card.type === "VISA" ? (
                                <img src={visaIcon} alt="Visa" className="card-brand-icon" />
                              ) : card.type === "MASTERCARD" ? (
                                <img src={masterIcon} alt="Mastercard" className="card-brand-icon" />
                              ) : card.type === "AMEX" ? (
                                <span className="payment-card-type amex-text">AMEX</span>
                              ) : (
                                <span className="payment-card-type">{card.type}</span>
                              )}
                            </div>
                            <div className="payment-card-details">
                              <div className="payment-card-number">
                                {card.masked}
                              </div>
                              <div className="payment-card-expiry">
                                Expires {card.exp}
                              </div>
                            </div>
                          </div>
                          <div className="payment-card-right">
                            <div className="payment-card-actions">
                              <button
                                className="payment-delete-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteCard(card.id);
                                }}
                                disabled={deletingCard === card.id}
                              >
                                {deletingCard === card.id ? (
                                  <>
                                    <span className="spinner-border spinner-border-sm me-1" />
                                    Deleting...
                                  </>
                                ) : (
                                  "Delete"
                                )}
                              </button>
                              <div className="payment-radio-container">
                                <input
                                  type="radio"
                                  name="paymentMethod"
                                  checked={selected === card.id}
                                  onChange={() => setSelected(card.id)}
                                  className="payment-method-radio"
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : !error ? (
                  <div className="payment-section">
                    <div className="text-center py-4">
                      <p className="text" style={{ fontSize: '16px', color: '#666', marginBottom: '10px' }}>
                        No Card Added Yet
                      </p>
                      <p className="text-muted" style={{ fontSize: '14px', marginBottom: '15px' }}>
                        You haven't added any saved cards. Use Direct Pay to add and save a card for future payments.
                      </p>
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={() => setSelected("directPay")}
                      >
                        Use Direct Pay to add a card
                      </button>
                    </div>
                  </div>
                ) : null}

                {/* Selected Method Info */}
                <div className="payment-section mt-4">
                  <div className="selected-method-info">
                    <div className="info-row">
                      <span className="info-label">
                        Selected Payment Method:
                      </span>
                      <span className="info-value">
                        {selected === "boc"
                          ? "BOC/Nations Trust Bank/WEBXPAY"
                          : selected === "directPay"
                          ? "Direct Pay"
                          : selectedCard
                          ? `${selectedCard.type} ${selectedCard.masked}`
                          : "None selected"}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Due Amount:</span>
                      <span className="info-value status-active">
                        {loadingPayments ? (
                          "Loading..."
                        ) : dueAmount !== "" ? (
                          `Rs. ${formatDisplayAmountLKR(dueAmount)}`
                        ) : (
                          `Rs. ${formatDisplayAmountLKR(0)}`
                        )}
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Payment Amount:</span>
                      <span className="info-value">
                        <div style={{ marginLeft: "auto", maxWidth: "180px" }}>
                          <input
                            type="number"
                            className={`form-control form-control-sm text-right${
                              paymentAmountTouched && paymentAmountError
                                ? " is-invalid"
                                : ""
                            }`}
                            min={MINIMUM_PAYMENT_AMOUNT}
                            step="0.01"
                            value={paymentAmount}
                            onChange={handlePaymentAmountChange}
                            onBlur={handlePaymentAmountBlur}
                            style={{
                              textAlign: "right",
                            }}
                          />
                          {paymentAmountTouched && paymentAmountError ? (
                            <small
                              className="text-danger d-block text-right mt-1"
                              style={{ lineHeight: 1.2 }}
                            >
                              {paymentAmountError}
                            </small>
                          ) : null}
                        </div>
                      </span>
                    </div>
                    <div className="info-row">
                      <span className="info-label">Telephone:</span>
                      <span className="info-value">{currentMobile}</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="payment-actions mt-4">
                  <div className="row">
                    <div className="col-md-6">
                      <button
                        className="btn btn-secondary btn-block"
                        onClick={() =>
                          history.push("/bill/0", { amount: payableAmount, mobile: currentMobile })
                        }
                      >
                        Back
                      </button>
                    </div>
                    <div className="col-md-6">
                      {selected === "directPay" ||
                      (selected !== "boc" && selectedCard) ? (
                        <button
                          type="button"
                          className="btn btn-primary btn-block"
                          disabled={
                            !hasValidPaymentAmount ||
                            !currentMobile ||
                            payableAmount < MINIMUM_PAYMENT_AMOUNT ||
                            loadingPayment ||
                            (selected !== "directPay" && selectedCard ? !walletId : false)
                          }
                          onClick={handlePayNow}
                        >
                          {loadingPayment ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm align-middle"
                                style={{ marginRight: "0.65rem" }}
                                role="status"
                                aria-hidden="true"
                              />
                              Processing...
                            </>
                          ) : (
                            "Pay Now"
                          )}
                        </button>
                      ) : (
                        <form action={payBillUrl} method="POST">
                          <input hidden value={currentMobile} name={"EventSource"} />
                          <input hidden value={payableAmount} name={"vpc_Amount"} />
                          <button
                            type="submit"
                            className="btn btn-primary btn-block"
                            disabled={
                              !hasValidPaymentAmount ||
                              !currentMobile ||
                              payableAmount < MINIMUM_PAYMENT_AMOUNT
                            }
                          >
                            Pay Now
                          </button>
                        </form>
                      )}
                    </div>
                  </div>
                </div>
            </>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  payments: state.accountDetails.payments,
  account: state.accountDetails.account_Details,
  loadingPayments: state.accountDetails.loadingPayments,
  service_list: state.accountDetails.service_list,
});

export default connect(mapStateToProps, { getPaymentDetails, getPaymentDetailsPrepaid })(PaymentMethod);
